import { UNNAMED_SPINCO } from "@mosaic/shared";
import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api, type DuplicateCandidate, type MergeProposal } from "../api/client.js";

/**
 * Which separation does this belong to — proposed, not decided.
 *
 * A CIK cannot tell a parent's two spin-offs apart, because it is the same on both. The
 * duplicate situations that leaves, and the filings parked because no situation could be
 * chosen, are the same decision, so they are on one page.
 *
 * **The automatic process runs right up to the write and stops here.** The proposal, its
 * reasoning and its evidence come from the server; the owner approves, denies, or
 * unchecks a row and approves. There is no free-text reason box: the reasoning is the
 * code's, the approval is the owner's, and a mandatory text field soon fills with
 * placeholders.
 */
export function ReviewPage() {
  const queryClient = useQueryClient();
  const duplicates = useQuery({
    queryKey: ["duplicate-situations"],
    queryFn: api.duplicateSituations,
  });
  const unplaced = useQuery({ queryKey: ["unplaced-filings"], queryFn: api.unplacedFilings });

  /**
   * Which situations are excluded from a proposal the owner is otherwise happy with.
   *
   * Held as exclusions rather than inclusions so the default is the proposal as offered,
   * and every deviation is something the owner actively did.
   */
  const [excluded, setExcluded] = useState<Record<string, boolean>>({});
  const [denied, setDenied] = useState<Record<string, boolean>>({});

  const merge = useMutation({
    mutationFn: (body: {
      keeperId: string;
      mergeIds: string[];
      approvedProposal: boolean;
    }) => api.mergeSituations(body),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["duplicate-situations"] });
      await queryClient.invalidateQueries({ queryKey: ["unplaced-filings"] });
    },
  });

  const groups = duplicates.data?.groups ?? [];
  const proposalCount = useMemo(
    () => groups.reduce((n, g) => n + g.proposals.length, 0),
    [groups],
  );

  return (
    <div className="read">
      <article className="article opshistory">
        <div className="panehd">
          <div>
            <h1>Reconciliation review</h1>
            <p className="deck">
              What the pipeline could not decide on its own, with what it would do and why.
              Nothing here has been written yet.
            </p>
          </div>
          <Link className="iconbtn" to="/ops">
            ← Operations
          </Link>
        </div>

        {duplicates.isPending && <p className="deck">Loading…</p>}
        {duplicates.error && <p className="err">{(duplicates.error as Error).message}</p>}

        <div className="sechd">Proposed folds</div>
        {!duplicates.isPending && proposalCount === 0 && (
          <p className="deck">
            {groups.length === 0
              ? "No company has more than one live situation."
              : "No group shares an identity strong enough to propose folding. The situations below are listed so you can see what is there."}
          </p>
        )}

        {groups.map((group) =>
          group.proposals.map((proposal) => {
            const key = `${group.cik}:${proposal.keeperId}`;
            const keeper = group.situations.find((s) => s.id === proposal.keeperId);
            const folds = proposal.foldIds
              .map((id) => group.situations.find((s) => s.id === id))
              .filter((s): s is DuplicateCandidate => Boolean(s));
            const kept = folds.filter((s) => !excluded[s.id]);
            if (denied[key]) return null;
            return (
              <section key={key} className="proposal">
                <div className="prophd">
                  <div>
                    <b>{group.company}</b>
                    {/*
                      A shared ticker names one distribution; a shared expected window is a
                      coincidence two separations by one parent can genuinely share, and
                      that proposal is the one to open the filings for.
                    */}
                    <span className={`strength ${proposal.strength}`}>
                      {proposal.strength === "identity" ? "shared identity" : "window only"}
                    </span>
                  </div>
                </div>
                <p className="prreason">{proposal.reason}</p>

                {keeper && <SituationRow situation={keeper} role="keeper" />}
                {folds.map((s) => (
                  <SituationRow
                    key={s.id}
                    situation={s}
                    role="fold"
                    excluded={Boolean(excluded[s.id])}
                    onToggle={() => setExcluded({ ...excluded, [s.id]: !excluded[s.id] })}
                  />
                ))}

                <p className="ops-actions">
                  <button
                    className="iconbtn"
                    disabled={kept.length === 0 || merge.isPending}
                    onClick={() =>
                      merge.mutate({
                        keeperId: proposal.keeperId,
                        mergeIds: kept.map((s) => s.id),
                        /*
                          Only an untouched proposal is an approval of the code's
                          reasoning. Unchecking a row makes it the owner's own decision,
                          and the recorded reason says so.
                        */
                        approvedProposal: kept.length === folds.length,
                      })
                    }
                  >
                    {merge.isPending
                      ? "Folding…"
                      : kept.length === folds.length
                        ? `Approve — fold ${kept.length}`
                        : `Fold ${kept.length} of ${folds.length}`}
                  </button>
                  <button className="iconbtn" onClick={() => setDenied({ ...denied, [key]: true })}>
                    Deny
                  </button>
                  {kept.length === 0 && (
                    <span className="hint">Every situation is unchecked — nothing to fold.</span>
                  )}
                </p>
              </section>
            );
          }),
        )}

        {/*
          A group the code will not propose a fold for, listed anyway, because the line
          above says it is. Several separations by one parent is ordinary.

          Read-only: the code has no fold to propose, so the owner gets the evidence, with
          the filings linking out as they do inside a proposal.
        */}
        {groups
          .filter((group) => group.proposals.length === 0)
          .map((group) => (
            <section key={group.cik} className="proposal listing">
              <div className="prophd">
                <div>
                  <b>{group.company}</b>
                </div>
              </div>
              <p className="prreason">
                <b>Nothing to approve here.</b> {group.situations.length} live situations
                under this company, and no two share a ticker, a set of terms or a SpinCo
                name — which is what a parent running several separations at once looks
                like. They are listed so you can see them; folding any of these would be
                your own call, from the situation pages.
              </p>
              {group.situations.map((s) => (
                <SituationRow key={s.id} situation={s} role="listing" />
              ))}
            </section>
          ))}

        {merge.error && <p className="err">{(merge.error as Error).message}</p>}
        {merge.data && (
          <div className="notice">
            <b>
              {merge.data.merged?.length ?? 0} situation
              {merge.data.merged?.length === 1 ? "" : "s"} folded in
            </b>
            {merge.data.filingsMoved ?? 0} filing(s) and {merge.data.factsMoved ?? 0} fact(s)
            moved across
            {merge.data.announcedOn ? `; announced ${merge.data.announcedOn}` : ""}.
            {(merge.data.userFactsNotMoved?.length ?? 0) > 0 && (
              <>
                {" "}
                Your own overrides were left where they are:{" "}
                {(merge.data.userFactsNotMoved ?? []).map((f) => f.kind).join(", ")}.
              </>
            )}
          </div>
        )}

        {/*
          The other half of the same decision. A parked filing is invisible everywhere
          else by construction — it is on no situation, so no situation page shows it.
        */}
        <div className="sechd">Filings waiting for a situation</div>
        <p className="deck">
          Their filer has several live situations and nothing could tell which one the
          document is about. They are indexed and searchable; only the radar attachment
          waits. A placement read resolves what identity matching cannot.
        </p>
        {unplaced.data && unplaced.data.total === 0 && (
          <p className="deck">Nothing is waiting.</p>
        )}
        {unplaced.data && unplaced.data.total > 0 && (
          <>
            <p className="fin-caveat">
              {unplaced.data.total} filing(s) across {unplaced.data.companies} compan
              {unplaced.data.companies === 1 ? "y" : "ies"}
              {unplaced.data.stuck > 0
                ? ` · ${unplaced.data.stuck} looked at more than twice and still unplaced`
                : ""}
              {unplaced.data.awaitingRead > 0
                ? ` · ${unplaced.data.awaitingRead} awaiting a placement read`
                : ""}
              {unplaced.data.total > unplaced.data.items.length
                ? ` · showing the first ${unplaced.data.items.length}`
                : ""}
            </p>
            <div className="tablewrap">
              <table className="dense">
                <thead>
                  <tr>
                    <th>Filing</th>
                    <th>Company</th>
                    <th>Filed</th>
                    <th>Candidates</th>
                    <th>Tries</th>
                    <th>Why it is here</th>
                  </tr>
                </thead>
                <tbody>
                  {unplaced.data.items.map((item) => (
                    <tr key={item.accession}>
                      <td>
                        <FilingLinks
                          accession={item.accession}
                          cik={item.cik}
                          label={`${item.formType} ${item.accession}`}
                        />
                      </td>
                      <td>{item.company ?? item.cik}</td>
                      <td>{item.filingDate}</td>
                      <td>{item.candidates}</td>
                      <td>{item.attempts}</td>
                      <td className="wrapcell">{item.reason}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
      </article>
    </div>
  );
}

/** One situation in a proposal, with everything a decision needs on the row. */
function SituationRow({
  situation,
  role,
  excluded,
  onToggle,
}: {
  situation: DuplicateCandidate;
  /**
   * `listing` is not a third kind of proposal — it is the absence of one. A row in a
   * group the code will not propose a fold for must stage nothing: no checkbox, no
   * "keep" marker, nothing that reads as a decision already taken.
   */
  role: "keeper" | "fold" | "listing";
  excluded?: boolean;
  onToggle?: () => void;
}) {
  const terms = [
    situation.ratio,
    situation.recordDate ? `record ${situation.recordDate}` : null,
    situation.distributionDate ? `distributed ${situation.distributionDate}` : null,
    situation.expectedCompletion ? `expected ${situation.expectedCompletion}` : null,
  ].filter(Boolean);

  return (
    <div className={`prrow${excluded ? " off" : ""}`}>
      <div className="prmark">
        {role === "keeper" && <span className="keeps">keep</span>}
        {role === "fold" && (
          <input
            type="checkbox"
            aria-label={`Fold in ${situation.id}`}
            checked={!excluded}
            onChange={onToggle}
          />
        )}
      </div>
      <div className="prbody">
        <div className="prtitle">
          <Link to={`/situations/${situation.id}`}>
            {situation.spincoName === UNNAMED_SPINCO
              ? situation.spincoTicker
                ? `SpinCo not yet named (${situation.spincoTicker})`
                : "SpinCo not yet named"
              : situation.spincoName}
          </Link>
          <span className="prmeta">
            {situation.stage}
            {situation.announcedOn ? ` · announced ${situation.announcedOn}` : ""}
            {` · ${situation.factCount} fact(s)`}
          </span>
        </div>
        {terms.length > 0 && <div className="prterms">{terms.join(" · ")}</div>}
        {/*
          The filings are the evidence, so they are on the row rather than a click away.
          A form type and a date cannot settle which separation a document is about; the
          document can.
        */}
        <div className="prfilings">
          {situation.filings.length === 0 ? (
            <span className="prmeta">no filings</span>
          ) : (
            situation.filings.map((f) => (
              <FilingLinks
                key={f.accession}
                accession={f.accession}
                cik={f.cik}
                label={`${f.formType} ${f.filingDate}`}
              />
            ))
          )}
        </div>
      </div>
    </div>
  );
}

/**
 * Both ways into a filing: Mosaic's reader, which carries the outline, the extracted text
 * and the citations, and EDGAR, which is the source of truth.
 */
function FilingLinks({
  accession,
  cik,
  label,
}: {
  accession: string;
  cik: string;
  label: string;
}) {
  // EDGAR drops the leading zeros in the path but keeps the dashes in the index filename.
  const edgar = `https://www.sec.gov/Archives/edgar/data/${Number(cik)}/${accession.replace(
    /-/g,
    "",
  )}/${accession}-index.htm`;
  return (
    <span className="flink">
      <Link to={`/filings/${accession}`}>{label}</Link>
      <a href={edgar} target="_blank" rel="noreferrer" title="Open on EDGAR">
        ↗
      </a>
    </span>
  );
}
